import { ChecklistItemProgress } from "@/components/checklist/ChecklistItemProgress";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAssignPhotos, useChecklist } from "@/hooks/use-checklist";
import type { Photo } from "@/schemas/photos";

/** In the viewer, for a PM: which checklist items this photo counts toward. Renders nothing for an album with no checklist. */
export function PhotoChecklist({ photo, projectId }: { photo: Photo; projectId: string }) {
  const { data, isPending, isError, refetch } = useChecklist(projectId);
  const assign = useAssignPhotos();
  const album = data?.albums.find((candidate) => candidate.albumId === photo.albumId);

  if (isPending) return <Skeleton className="h-10 w-full" />;
  if (isError) {
    return (
      <Button variant="ghost" size="sm" onClick={() => refetch()}>
        Could not load the checklist. Try again
      </Button>
    );
  }
  if (!album) return null;

  return (
    <section data-testid="photo-checklist" className="space-y-3 border-t pt-4">
      <h3 className="text-xs text-muted-foreground">Counts toward</h3>
      {album.items.map((item) => {
        const assigned = item.photos.some((linked) => linked.id === photo.id);
        return (
          <div key={item.id} data-testid="photo-checklist-item" className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm">{item.name}</span>
              <Button
                variant={assigned ? "default" : "outline"}
                size="sm"
                aria-pressed={assigned}
                data-testid="assign-item"
                disabled={assign.isPending}
                onClick={() =>
                  assign.mutate({
                    itemId: item.id,
                    request: { photoIds: [photo.id], assigned: !assigned },
                  })
                }
              >
                {assigned ? "Assigned" : "Assign"}
              </Button>
            </div>
            <ChecklistItemProgress
              requiredCount={item.requiredCount}
              assignedCount={item.assignedCount}
            />
          </div>
        );
      })}
    </section>
  );
}
